import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { formatDateTime } from '@src/shell/utils/Formatter'
import { useToast } from '@src/shared/hooks/useToast'
import { useClearQueue, useDeleteMessages, useQueueMessages, useRequeueAll, useRequeueMessages } from '@src/tools/dead-letter/api/queues-api'
import { QueueMessage } from '@src/tools/dead-letter/types/QueueTypes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QueueMessageDetailDialog from './QueueMessageDetailDialog'

interface Props {
  storageAccountName: string
  queueName: string
  friendlyName: string
  totalMessageCount: number
}

const PoisonQueueMessages = ({ storageAccountName, queueName, friendlyName, totalMessageCount }: Props) => {
  const { t } = useTranslation()
  const [messages, isLoading] = useQueueMessages(storageAccountName, queueName)
  const [requeueSelected, isRequeueingSelected, isRequeueSelectedError, resetRequeueSelected] = useRequeueMessages()
  const [requeueAll, isRequeueingAll, isRequeueAllError, resetRequeueAll] = useRequeueAll()
  const [deleteSelected, isDeletingSelected, isDeleteSelectedError, resetDeleteSelected] = useDeleteMessages()
  const [clearQueue, isClearing, isClearError, resetClear] = useClearQueue()

  const isBusy = isLoading || isRequeueingSelected || isRequeueingAll || isDeletingSelected || isClearing
  const { showSuccess, ToastComponent } = useToast()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmRequeueAll, setConfirmRequeueAll] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const [detailMessage, setDetailMessage] = useState<QueueMessage | null>(null)

  useEffect(() => {
    setSelectedIds(new Set())
  }, [storageAccountName, queueName])

  const allSelected = useMemo(() => messages.length > 0 && selectedIds.size === messages.length, [messages, selectedIds])

  const toggleSelect = useCallback((messageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(messageId)) next.delete(messageId)
      else next.add(messageId)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(messages.map((m) => m.messageId)))
    }
  }, [allSelected, messages])

  const handleRequeueSelected = useCallback(async () => {
    resetRequeueSelected()
    try {
      const result = await requeueSelected({
        storageAccountName,
        queueName,
        messageIds: [...selectedIds],
      })
      showSuccess(t('queues.requeueSuccess', { count: result.requeuedCount }))
      setSelectedIds(new Set())
    } catch {
      // handled by isRequeueSelectedError
    }
  }, [selectedIds, requeueSelected, storageAccountName, queueName, t, resetRequeueSelected, showSuccess])

  const handleDeleteSelected = useCallback(async () => {
    resetDeleteSelected()
    try {
      const result = await deleteSelected({
        storageAccountName,
        queueName,
        messageIds: [...selectedIds],
      })
      showSuccess(t('queues.deleteSuccess', { count: result.deletedCount }))
      setSelectedIds(new Set())
    } catch {
      // handled by isDeleteSelectedError
    }
  }, [selectedIds, deleteSelected, storageAccountName, queueName, t, resetDeleteSelected, showSuccess])

  const handleConfirmRequeueAll = useCallback(async () => {
    resetRequeueAll()
    try {
      const result = await requeueAll({ storageAccountName, queueName })
      showSuccess(t('queues.requeueSuccess', { count: result.requeuedCount }))
      setSelectedIds(new Set())
    } catch {
      // handled by isRequeueAllError
    } finally {
      setConfirmRequeueAll(false)
    }
  }, [requeueAll, storageAccountName, queueName, t, resetRequeueAll, showSuccess])

  const handleConfirmClear = useCallback(async () => {
    resetClear()
    try {
      await clearQueue({ storageAccountName, queueName })
      showSuccess(t('queues.clearSuccess'))
      setSelectedIds(new Set())
    } catch {
      // handled by isClearError
    } finally {
      setConfirmClear(false)
    }
  }, [clearQueue, storageAccountName, queueName, t, resetClear, showSuccess])

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{queueName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {friendlyName} ({storageAccountName}) · {messages.length} messages
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AsyncActionButton
            variant="contained"
            onClick={handleRequeueSelected}
            disabled={selectedIds.size === 0 || isBusy}
            isPending={isRequeueingSelected}
            isError={isRequeueSelectedError}
            size="small"
          >
            {t('queues.requeueSelected')} ({selectedIds.size})
          </AsyncActionButton>
          <AsyncActionButton
            variant="outlined"
            color="error"
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0 || isBusy}
            isPending={isDeletingSelected}
            isError={isDeleteSelectedError}
            size="small"
          >
            {t('queues.deleteSelected')} ({selectedIds.size})
          </AsyncActionButton>
          <AsyncActionButton
            variant="contained"
            color="error"
            onClick={() => setConfirmRequeueAll(true)}
            disabled={totalMessageCount === 0 || isBusy}
            isPending={isRequeueingAll}
            isError={isRequeueAllError}
            size="small"
          >
            {t('queues.requeueAll')} ({totalMessageCount})
          </AsyncActionButton>
          <AsyncActionButton
            variant="outlined"
            color="error"
            onClick={() => setConfirmClear(true)}
            disabled={totalMessageCount === 0 || isBusy}
            isPending={isClearing}
            isError={isClearError}
            size="small"
          >
            {t('queues.clear')}
          </AsyncActionButton>
        </Box>
      </Box>

      <ToastComponent />
      {(isRequeueSelectedError || isDeleteSelectedError || isRequeueAllError || isClearError) && <Alert severity="error" sx={{ mb: 1 }}>{t('queues.actionError')}</Alert>}

      <TableContainer sx={{ flexGrow: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: 'secondary.main', color: 'white' }}>
                <Checkbox checked={allSelected} indeterminate={selectedIds.size > 0 && !allSelected} onChange={toggleSelectAll} sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }} />
              </TableCell>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>{t('queues.messageId')}</TableCell>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>{t('queues.enqueuedOn')}</TableCell>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>{t('queues.dequeueCount')}</TableCell>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>{t('queues.contentPreview')}</TableCell>
              <TableCell sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 'bold', width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, border: 'none' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
            {messages.map((msg) => (
              <TableRow key={msg.messageId} hover>
                <TableCell padding="checkbox">
                  <Checkbox checked={selectedIds.has(msg.messageId)} onChange={() => toggleSelect(msg.messageId)} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {msg.messageId.substring(0, 12)}...
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>
                  {formatDateTime(new Date(msg.enqueuedOn))}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{msg.dequeueCount}</TableCell>
                <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem', color: 'text.secondary' }}>
                  {msg.content}
                </TableCell>
                <TableCell>
                  <Tooltip title={t('queues.viewDetail')}>
                    <IconButton size="small" onClick={() => setDetailMessage(msg)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <QueueMessageDetailDialog
        message={detailMessage}
        open={!!detailMessage}
        onClose={() => setDetailMessage(null)}
      />

      <Dialog open={confirmRequeueAll} onClose={() => setConfirmRequeueAll(false)}>
        <DialogTitle>{t('queues.confirmRequeueAllTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('queues.confirmRequeueAll', { count: totalMessageCount })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRequeueAll(false)}>{t('common.cancel')}</Button>
          <AsyncActionButton onClick={handleConfirmRequeueAll} variant="contained" color="error" disabled={isBusy} isPending={isRequeueingAll} isError={isRequeueAllError}>
            {t('common.confirm')}
          </AsyncActionButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>{t('queues.confirmClearTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('queues.confirmClear')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>{t('common.cancel')}</Button>
          <AsyncActionButton onClick={handleConfirmClear} variant="contained" color="error" disabled={isBusy} isPending={isClearing} isError={isClearError}>
            {t('queues.clear')}
          </AsyncActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PoisonQueueMessages
